/**
 * components/locks/GlbView.tsx — GLB render helper (T14).
 *
 * Component shared by locks (ConnectorJoist / ConnectorHeader):
 * load a GLB via `useGLTF` (drei global cache) and render one
 * CLONE, so each instance has its own independent scene.
 *
 * If `url` is null → return `null` (caller handles fallback box
 * parametric, SPEC R6: no errors without GLB).
 *
 * Hook rule: `useGLTF` is called unconditionally in the sub-component
 * `GlbViewInner`, which is ONLY mounted when url exists (safe pattern).
 *
 *NOTE Suspense: The caller MUST wrap this component in a
 * `<Suspense>` (LockScene/T15) otherwise the suspension goes up to the Canvas.
 *
 * Boundary rule: layer THREE (render) — no calculation of positions.*/

import { useGLTF } from "@react-three/drei";
import { useEffect, useMemo } from "react";
import * as THREE from "three";
import { COLORS } from "../../config/colors";
import { CONNECTOR_EDGES } from "../../config/defaults";

export interface GlbViewProps {
  /** GLB URL blob; null → null (caller-handled fallback).*/
  url: string | null;
}

/**
 * Render the cloned GLB to the parent group source.
 * The caller sets position/quaternion on the group that wraps GlbView.*/
export function GlbView({ url }: GlbViewProps) {
  //Guard: no url → no loading (caller's fallback box).
  if (!url) return null;
  return <GlbViewInner url={url} />;
}

//─── Internal sub-component (unconditional hook) ───────────

function GlbViewInner({ url }: { url: string }) {
  const { scene } = useGLTF(url);
  //FIX D RIMOSSO (25-09-2026, richiesta utente): ricentrava la mesh sul
  //proprio bounding box reale per sistemare l'incastro joist/header, ma
  //`holeToFastenerAnchor` (model/fasteners.ts) posiziona le viti con
  //`hole.position` dal JSON rispetto all'origine NATIVA della mesh (mai
  //ricentrata) — ricentrare qui il corpo scollegava le viti dai fori
  //(erano corrette prima di FIX D, secondo l'utente). Boundary rule (SPEC
  //§6): questo componente e' render puro, NESSUN calcolo di posizione —
  //il ricentraggio era comunque nel posto sbagliato. La correzione
  //dell'incastro resta in `connectors.ts` (FIX F, flush sul piano trave)
  //e in `ConnectorMesh.tsx` (FIX C/E, rotazioni 180°).
  const cloned = useMemo(() => scene.clone(true), [scene]);
  const edges = useMemo(() => buildEdges(cloned), [cloned]);

  //Le geometrie degli spigoli sono create qui, non da useGLTF: la cache di drei
  //non le conosce e nessuno le liberebbe al posto nostro.
  useEffect(
    () => () => {
      edges.group.traverse((child) => {
        const lines = child as THREE.LineSegments;
        if (lines.isLineSegments) lines.geometry.dispose();
      });
      edges.material.dispose();
    },
    [edges],
  );

  return (
    <>
      <primitive object={cloned} />
      <primitive object={edges.group} />
    </>
  );
}

/**
 * Contorno tecnico del GLB: una EdgesGeometry per ogni mesh, portata nel frame
 * della radice clonata con la matrice della mesh, cosi' le linee restano
 * incollate al corpo qualunque gerarchia abbia il file.*/
function buildEdges(root: THREE.Object3D): { group: THREE.Group; material: THREE.LineBasicMaterial } {
  const group = new THREE.Group();
  const material = new THREE.LineBasicMaterial({
    color: COLORS.connectorEdge,
    transparent: CONNECTOR_EDGES.opacity < 1,
    opacity: CONNECTOR_EDGES.opacity,
  });

  root.updateMatrixWorld(true);
  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const lines = new THREE.LineSegments(
      new THREE.EdgesGeometry(mesh.geometry, CONNECTOR_EDGES.thresholdDeg),
      material,
    );
    lines.applyMatrix4(mesh.matrixWorld);
    group.add(lines);
  });

  return { group, material };
}
